// Rolling gallery of past work for the home page.
//
// A strip of small cards rather than one large photo — it shows more of the
// back catalogue at once and keeps the hero as the page's focal point.
//
// It pans at one constant speed and nothing interrupts it: no hover pause,
// no dragging, no clicking through. The list is rendered twice and the
// position wraps by exactly one copy's width, so the loop has no visible
// seam, and the speed is in pixels per second, so adding photos makes the
// loop longer rather than faster.
//
// Renders nothing when there are no featured images, so a brand-new shop
// doesn't show an empty band under a heading.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { getFeaturedImages } from '../services/api';
import type { FeaturedImage } from '../types';

const SPEED_PX_PER_SEC = 28; // slow enough to read a photo as it passes
const GAP_PX = 16; // matches gap-4 on the track
// Photos decoded at once. Enough to stay ahead of the pan without putting
// the whole gallery on the wire in one go.
const DECODE_CONCURRENCY = 3;
// Enough to fill the strip on the widest layout before the pan starts.
const INITIAL_DECODE = 8;
// Photos kept ready beyond the one currently reaching the edge.
const LOAD_AHEAD = 5;

const FeaturedCarousel: React.FC = () => {
  const [images, setImages] = useState<FeaturedImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [reducedMotion, setReducedMotion] = useState(false);
  // Indices whose photo has finished decoding and is safe to put in the DOM.
  const [ready, setReady] = useState<Set<number>>(new Set());
  // How far into the list decoding is allowed to run. Grows as the pan
  // advances rather than starting at the whole gallery.
  const [target, setTarget] = useState(INITIAL_DECODE);
  const targetRef = useRef(INITIAL_DECODE);
  // Next index to hand to a decode worker. Shared by all of them, and only
  // ever incremented, so no photo is decoded twice.
  const nextIndex = useRef(0);
  // How many times the list repeats to make one seamless copy, how wide one
  // card is, and how wide a copy ended up. All measured from the DOM.
  const [pan, setPan] = useState({ repeat: 1, cardWidth: 0, copyWidth: 0 });
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    getFeaturedImages()
      .then(list => {
        if (!cancelled) setImages(list);
      })
      // Non-fatal: the section just stays hidden.
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Fetch and decode photos off to the side, and only mount an <img> once
   * its turn is done.
   *
   * Mounting the image first and letting the browser sort it out is what
   * made the pan stutter: turning a photo into pixels stalls the main
   * thread long enough to see, even when the file itself is already
   * cached. decode() does that work before the element exists, so the
   * frame a photo appears in costs no more than any other.
   *
   * Only runs as far as `target`, which starts at a screenful and grows
   * with the pan. Decoding the whole gallery up front made the first paint
   * wait on work for photos that were minutes away from being seen.
   */
  useEffect(() => {
    if (images.length === 0) return;
    let cancelled = false;

    const work = async () => {
      while (!cancelled) {
        const index = nextIndex.current;
        if (index >= Math.min(target, images.length)) return;
        nextIndex.current = index + 1;
        try {
          const img = new Image();
          img.src = images[index].url;
          await img.decode();
        } catch {
          // A photo that won't decode simply never appears. The strip
          // keeps moving and the frame stays blank.
          continue;
        }
        if (cancelled) return;
        setReady(prev => new Set(prev).add(index));
      }
    };

    for (let i = 0; i < DECODE_CONCURRENCY; i++) work();
    return () => {
      cancelled = true;
    };
  }, [images, target]);

  // A new gallery starts decoding from the beginning again.
  useEffect(() => {
    nextIndex.current = 0;
    targetRef.current = INITIAL_DECODE;
    setTarget(INITIAL_DECODE);
    setReady(new Set());
  }, [images]);

  // A short list can be narrower than the viewport, which would leave a gap
  // mid-loop, so the list repeats until one copy covers the strip.
  const measure = useCallback(() => {
    const wrap = wrapRef.current;
    const card = wrap?.querySelector('figure');
    if (!card || !wrap || images.length === 0) return;
    const cardWidth = card.getBoundingClientRect().width + GAP_PX;
    const listWidth = images.length * cardWidth;
    const repeat = Math.max(1, Math.ceil(wrap.clientWidth / listWidth));
    setPan({ repeat, cardWidth, copyWidth: listWidth * repeat });
  }, [images.length]);

  useLayoutEffect(measure, [measure]);

  useEffect(() => {
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReducedMotion(query.matches);
    apply();
    query.addEventListener('change', apply);
    return () => query.removeEventListener('change', apply);
  }, []);

  const { cardWidth, copyWidth } = pan;
  const total = images.length;

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || reducedMotion || copyWidth === 0) return;

    let frame = 0;
    let last = performance.now();
    // Sub-pixel steps at this speed, so the position is tracked as a float
    // and only handed to scrollLeft, which may round it.
    let position = el.scrollLeft;

    const tick = (now: number) => {
      position += (SPEED_PX_PER_SEC * (now - last)) / 1000;
      last = now;
      // Wrapping by exactly one copy lands on identical content, so the
      // restart is invisible.
      if (position >= copyWidth) position -= copyWidth;
      el.scrollLeft = position;

      // Keep decoding a few photos ahead of what has reached the edge.
      // Guarded on an actual increase so this doesn't touch React state on
      // every frame — it fires at most once per photo.
      if (cardWidth > 0 && targetRef.current < total) {
        const reached = Math.floor((position + el.clientWidth) / cardWidth);
        const want = Math.min(total, reached + LOAD_AHEAD);
        if (want > targetRef.current) {
          targetRef.current = want;
          setTarget(want);
        }
      }

      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [reducedMotion, copyWidth, cardWidth, total]);

  if (loading || images.length === 0) return null;

  // Two copies of the repeated list: the position wraps by one copy, so it
  // can restart without a visible jump.
  const copies = Array.from({ length: 2 * pan.repeat }, (_, i) => i);

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
      <div className="flex items-baseline justify-between mb-5">
        <h2 className="text-3xl">Recent work</h2>
        <span className="text-sm text-text-light">{images.length} pieces</span>
      </div>

      {/* Hidden overflow, not auto: the strip is scrolled from script only,
          so a stray swipe or trackpad nudge can't leave it somewhere the
          pan then has to recover from. Reduced motion is the exception —
          with no pan running, scrolling is the only way through. */}
      <div
        ref={wrapRef}
        className={
          reducedMotion
            ? 'overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
            : 'overflow-hidden'
        }
      >
        <div className="flex gap-4 w-max">
          {copies.map(copy =>
            images.map((img, i) => (
              <figure
                key={`${copy}-${img.key}`}
                // Only the first pass through the list is real content; the
                // rest are there to fill the loop.
                aria-hidden={copy > 0 || undefined}
                className="shrink-0 w-40 sm:w-48 lg:w-56"
              >
                {/* The frame is always here, so the strip is on screen
                    immediately and each photo drops into a space already
                    laid out for it. */}
                <div className="aspect-square rounded overflow-hidden bg-background-dark border border-background-dark">
                  {ready.has(i) && (
                    <img
                      src={img.url}
                      alt={copy === 0 ? `Featured piece ${i + 1}` : ''}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
              </figure>
            ))
          )}
        </div>
      </div>
    </section>
  );
};

export default FeaturedCarousel;
